import { useRef, useState } from "react";
import { ApiError, useApiFetch } from "../../api/client";

interface StoredFile {
  intakeFileId: number;
  batchGuid: string;
  originalFileName: string;
  fileSizeBytes: number;
  sha256Hex: string;
  uploadedUtc: string;
}

interface DuplicateResult {
  file: string;
  existingBatchGuid: string;
  existingCommittedUtc: string | null;
}

function formatSize(bytes: number): string {
  const kb = bytes / 1024;
  return kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(kb))} KB`;
}

export function TdsLane() {
  const apiFetch = useApiFetch();
  const [stored, setStored] = useState<StoredFile[]>([]);
  const [duplicates, setDuplicates] = useState<DuplicateResult[]>([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const uploadFiles = async (fileList: FileList | null) => {
    const files = [...(fileList ?? [])];
    if (!files.length) return;
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      const res = await apiFetch("tds/files", { method: "POST", body: form });
      const newlyStored: StoredFile[] = await res.json();
      setStored((prev) => [...newlyStored, ...prev]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const detail = (err.body as { detail?: { existingBatchGuid: string; existingCommittedUtc: string | null } })?.detail;
        setDuplicates((prev) => [
          {
            file: files[0]?.name ?? "file",
            existingBatchGuid: detail?.existingBatchGuid ?? "unknown",
            existingCommittedUtc: detail?.existingCommittedUtc ?? null,
          },
          ...prev,
        ]);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setUploading(false);
    }
  };

  const viewFile = async (intakeFileId: number) => {
    setViewingId(intakeFileId);
    try {
      const res = await apiFetch(`tds/files/${intakeFileId}`);
      const detail: { readUrl: string } = await res.json();
      window.open(detail.readUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setViewingId(null);
    }
  };

  return (
    <section className="lane on">
      <h2>Technical Data Sheets</h2>
      <p className="lede">
        Drop data sheets in and they're stored for the RESIN8 team to review by
        hand. Automatic grade matching and property extraction aren't built
        yet — this lane just makes sure sheets stop getting lost while that
        approach is settled.
      </p>

      <div className="sec">
        <div
          className={`drop${dragging ? " hot" : ""}`}
          onDragEnter={(e) => { e.preventDefault(); setDragging(true); }}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragging(false); }}
          onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
        >
          <span className="material-symbols-sharp">{uploading ? "progress_activity" : "lab_profile"}</span>
          <h3>{uploading ? "Uploading…" : "Drop Data Sheets Here"}</h3>
          <p>PDF, one or many at a time.</p>
          <button className="btn" disabled={uploading} onClick={() => inputRef.current?.click()}>
            <span className="material-symbols-sharp">folder_open</span>Choose Files
          </button>
          <input
            ref={inputRef} type="file" accept=".pdf" multiple
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          />
        </div>
      </div>

      {error && (
        <div className="sec">
          <div className="gate">
            <span className="msg" style={{ color: "var(--salmon)" }}>{error}</span>
          </div>
        </div>
      )}

      {duplicates.length > 0 && (
        <div className="sec">
          <div className="sec-hd"><h3>Already on File</h3></div>
          <div className="checks">
            {duplicates.map((d, i) => (
              <div className="check fail" key={i}>
                <div className="ic"><span className="material-symbols-sharp">content_copy</span></div>
                <div className="bd">
                  <div className="ti">{d.file}</div>
                  <div className="ds">
                    Identical content already stored in batch {d.existingBatchGuid}
                    {d.existingCommittedUtc && ` on ${new Date(d.existingCommittedUtc).toLocaleDateString()}`}.
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stored.length > 0 && (
        <div className="sec">
          <div className="sec-hd">
            <h3>Stored This Session</h3>
            <span className="note">{stored.length} sheet{stored.length > 1 ? "s" : ""}</span>
          </div>
          <div className="card" style={{ padding: 0, overflow: "hidden" }}>
            <table>
              <thead><tr><th>File</th><th style={{ width: "1%" }}>Size</th><th style={{ width: "1%" }}>Uploaded</th><th style={{ width: "1%" }}></th></tr></thead>
              <tbody>
                {stored.map((f) => (
                  <tr key={f.intakeFileId}>
                    <td>{f.originalFileName}</td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--grey)" }}>{formatSize(f.fileSizeBytes)}</td>
                    <td style={{ whiteSpace: "nowrap", color: "var(--grey)" }}>{new Date(f.uploadedUtc).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                    <td>
                      <button className="linkbtn" disabled={viewingId === f.intakeFileId} onClick={() => viewFile(f.intakeFileId)}>
                        {viewingId === f.intakeFileId ? "Opening…" : "View"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
